import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import UploadHotelBiddingCriteria from '~/components/GoogleAdsHotelBidding/UploadHotelBiddingCriteria';

import useAppNavigation from '~/hooks/useAppNavigation';

import {
  AdCustomer,
  HotelGroupViewRecord,
  deleteHotelGroupCriteria,
  fetchHotelGroupView,
  getCustomers,
  updateHotelGroupCriteria,
} from '~/services/PublicOfferFeedService';

type ViewMode = 'view' | 'update' | 'delete';

function HotelGroupView() {
  const location = useLocation();
  const { push } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();
  const searchParams = new URLSearchParams(location.search);
  const propertyIdsParam = searchParams.get('propertyIds') || '';
  const customerIdParam = searchParams.get('customerId') || '';
  const [searchResults, setSearchResults] = useState<Array<HotelGroupViewRecord>>([]);
  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');
  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const sortModelRef = useRef<{ field: string; sort: string }>({ field: 'campaignName', sort: 'asc' });
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('view');

  const [customers, setCustomers] = useState<Array<AdCustomer>>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [customersFetchingState, setCustomersFetchingState] = useState<Utils.FetchingState>('idle');
  const [modifiedFields, setModifiedFields] = useState<
    Array<{
      criterionId: string;
      target: number;
      hotelId: string;
      resourceName: string;
      campaignId: string;
      adGroupId: string;
      previousTarget?: number;
    }>
  >([]);

  const [deletedFields, setDeletedFields] = useState<
    Array<{
      criterionId: string;
      hotelId: string;
      resourceName: string;
      campaignId: string;
      adGroupId: string;
    }>
  >([]);

  const columns: Array<GridColDef> = [
    {
      field: 'hotelId',
      headerName: 'Hotel ID',
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 1,
      minWidth: 100,
      disableExport: true,
    },
    {
      field: 'campaignId',
      headerName: 'Campaign Id',
      flex: 2,
      minWidth: 100,
    },
    {
      field: 'campaignName',
      headerName: 'Campaign Name',
      flex: 2,
      minWidth: 200,
      disableExport: true,
    },
    {
      field: 'adGroupName',
      headerName: 'Ad Group Name',
      flex: 2,
      minWidth: 200,
      disableExport: true,
    },
    {
      field: 'percentCpcBid',
      headerName: 'Bid Percentage',
      flex: 1,
      minWidth: 150,
      editable: viewMode === 'update',
      type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2) || '0.00',
      preProcessEditCellProps: (params) => {
        const hasError = params.props.value < 0 || params.props.value > 100;
        return { ...params.props, error: hasError };
      },
    },
  ];

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setCustomersFetchingState('loading');
        const response = await getCustomers();
        setCustomers(response.result);

        if (customerIdParam && response.result.some((customer) => customer.id === customerIdParam)) {
          setSelectedCustomerId(customerIdParam);
        } else if (response.result.length > 0) {
          setSelectedCustomerId(response.result[0].id);
        }
        setCustomersFetchingState('success');
      } catch (err) {
        console.error('Error fetching customers:', err);
        setCustomersFetchingState('failed');
      }
    };

    fetchCustomers();
  }, [customerIdParam]);

  const fetchHotelData = useCallback(
    async (propertyIds?: Array<string>, limit?: number, page?: number) => {
      try {
        setFetchingState('loading');

        let data = [];
        if (selectedCustomerId) {
          const response = await fetchHotelGroupView({
            customerId: selectedCustomerId,
            propertyIds: propertyIds,
            page,
            limit,
          });
          data = response.result;
          setTotal(response.count);
        }

        setSearchResults(data);
        setFetchingState('success');
      } catch (err) {
        setFetchingState('failed');
      }
    },
    [selectedCustomerId],
  );

  useEffect(() => {
    const propertyIdsArray = propertyIdsParam
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id);

    fetchHotelData(propertyIdsArray, pageSize, pageNum);
  }, [propertyIdsParam, pageNum, pageSize, selectedCustomerId, fetchHotelData]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPageNum(model.page);
    setPageSize(model.pageSize);
  };

  // api not supported yet
  const handleSortModelChange = (sortModel: GridSortModel) => {
    sortModelRef.current = {
      field: sortModel?.[0]?.field || 'campaignName',
      sort: sortModel?.[0]?.sort || 'asc',
    };
  };

  const handleCustomerChange = (customerId: string) => {
    setSelectedCustomerId(customerId);

    const newSearchParams = new URLSearchParams(location.search);
    if (customerId) {
      newSearchParams.set('customerId', customerId);
    } else {
      newSearchParams.delete('customerId');
    }

    const newUrl = `${location.pathname}?${newSearchParams.toString()}`;
    push(newUrl);
  };

  const processRowUpdate = useCallback(
    (newRow: HotelGroupViewRecord, oldRow: HotelGroupViewRecord) => {
      if (newRow.percentCpcBid !== oldRow.percentCpcBid) {
        const { criterionId, percentCpcBid, hotelId, resourceName, adGroupId, campaignId } = newRow;
        const previousTarget = oldRow.percentCpcBid;

        const existingIndex = modifiedFields.findIndex((field) => field.criterionId === criterionId);

        if (existingIndex >= 0) {
          const updatedFields = [...modifiedFields];
          updatedFields[existingIndex] = {
            criterionId,
            target: percentCpcBid,
            hotelId,
            resourceName,
            adGroupId,
            campaignId,
            previousTarget,
          };
          setModifiedFields(updatedFields);
        } else {
          setModifiedFields((prev) => [
            ...prev,
            { criterionId, target: percentCpcBid, hotelId, resourceName, adGroupId, campaignId, previousTarget },
          ]);
        }

        // Auto-select the row when bid percentage is changed in update mode
        if (viewMode === 'update' && !selectionModel.includes(criterionId)) {
          setSelectionModel((prev) => [...prev, criterionId]);
        }
      }

      return newRow;
    },
    [modifiedFields, viewMode, selectionModel],
  );

  const handleSaveChanges = useCallback(async () => {
    if (modifiedFields.length === 0) {
      return;
    }

    try {
      setFetchingState('loading');
      await updateHotelGroupCriteria({ customerId: selectedCustomerId, updates: modifiedFields });
      setModifiedFields([]);
      setSelectionModel([]);
      await fetchHotelData();
      setFetchingState('success');
      enqueueSnackbar('Changes saved successfully', { variant: 'success' });
    } catch (error) {
      setFetchingState('failed');
      enqueueSnackbar('Error saving changes', { variant: 'error' });
    }
  }, [selectedCustomerId, modifiedFields, fetchHotelData, enqueueSnackbar]);

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
      if (newMode !== 'update') {
        setModifiedFields([]);
        setSelectionModel([]);
      }
      if (newMode !== 'delete') {
        setDeletedFields([]);
        setSelectionModel([]);
      }
    }
  };

  const handleSelectionChange = useCallback(
    (newSelectionModel: Array<string>) => {
      setSelectionModel(newSelectionModel);
      if (viewMode === 'delete') {
        const newDeletedFields = newSelectionModel
          .map((criterionId) => {
            const row = searchResults.find((result) => result.criterionId === criterionId);
            if (row) {
              return {
                criterionId: row.criterionId,
                hotelId: row.hotelId,
                resourceName: row.resourceName,
                campaignId: row.campaignId,
                adGroupId: row.adGroupId,
              };
            }
            return null;
          })
          .filter(Boolean);

        setDeletedFields(newDeletedFields);
      }
    },
    [viewMode, searchResults],
  );

  const handleDeleteSelected = useCallback(async () => {
    if (deletedFields.length === 0) {
      return;
    }

    try {
      setFetchingState('loading');
      await deleteHotelGroupCriteria({ customerId: selectedCustomerId, items: deletedFields });
      setSelectionModel([]);
      setDeletedFields([]);
      await fetchHotelData();
      setFetchingState('success');
      enqueueSnackbar('Selected items deleted successfully', { variant: 'success' });
    } catch (error) {
      setFetchingState('failed');
      enqueueSnackbar('Error deleting selected items', { variant: 'error' });
    }
  }, [selectedCustomerId, deletedFields, fetchHotelData, enqueueSnackbar]);

  const getActionButton = () => {
    switch (viewMode) {
      case 'update':
        return (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveChanges}
            disabled={modifiedFields.length === 0}
          >
            Save Changes ({modifiedFields.length})
          </Button>
        );
      case 'delete':
        return (
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSelected}
            disabled={deletedFields.length === 0}
          >
            Delete Selected ({deletedFields.length})
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ m: 3 }}>
      <Paper sx={{ padding: 2 }}>
        <Box mb={2}>
          <FormControl fullWidth>
            <InputLabel id="customer-select-label">Select Customer Account</InputLabel>
            <Select
              labelId="customer-select-label"
              id="customer-select"
              value={selectedCustomerId}
              label="Select Customer Account"
              onChange={(e) => handleCustomerChange(e.target.value)}
              disabled={customersFetchingState === 'loading'}
            >
              {customersFetchingState === 'loading' ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                  Loading customers...
                </MenuItem>
              ) : (
                customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.descriptiveName} ({customer.id})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </Paper>
      <UploadHotelBiddingCriteria customerId={selectedCustomerId} />
      <Paper sx={{ padding: 2, my: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <PageSubheader title="Google Ads Hotel Groups" />
          <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} aria-label="view mode">
            <ToggleButton value="view" aria-label="view mode">
              View
            </ToggleButton>
            <ToggleButton value="update" aria-label="update mode">
              Update
            </ToggleButton>
            <ToggleButton value="delete" aria-label="delete mode">
              Delete
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <DataGrid
          rows={searchResults}
          columns={columns}
          loading={fetchingState === 'loading'}
          pagination
          paginationMode="server"
          pageSizeOptions={[20, 100, 500, 1000, 2000]}
          rowCount={total}
          getRowId={(row) => row.criterionId}
          onPaginationModelChange={handlePaginationModelChange}
          autoHeight
          disableRowSelectionOnClick
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          checkboxSelection={viewMode !== 'view'}
          onRowSelectionModelChange={handleSelectionChange}
          rowSelectionModel={selectionModel}
          processRowUpdate={viewMode === 'update' ? processRowUpdate : undefined}
          slots={{
            toolbar: () => {
              return (
                <GridToolbarContainer>
                  <GridToolbarColumnsButton />
                  <GridToolbarExport />
                </GridToolbarContainer>
              );
            },
          }}
        />

        {viewMode !== 'view' && (
          <Box display="flex" justifyContent="right" alignItems="center" sx={{ my: 3 }}>
            <Box display="flex" alignItems="center" gap={2}>
              {viewMode === 'update' && (
                <Typography variant="body2" color="text.secondary">
                  {modifiedFields.length} field(s) modified
                </Typography>
              )}
              {viewMode === 'delete' && (
                <Typography variant="body2" color="text.secondary">
                  {deletedFields.length} row(s) selected
                </Typography>
              )}
              {getActionButton()}
            </Box>
          </Box>
        )}
      </Paper>
      <Paper sx={{ padding: 2, my: 2 }} id="history" className="branded-panel">
        <PageSubheader title="Hotel Bid Modification History" />

        <Box mt={1}>
          <Button
            component={RouterLink}
            to={`/marketing/google-ads/hotel-bid-modification-history?customerId=${selectedCustomerId}`}
            variant="outlined"
            sx={{ mt: 2 }}
          >
            View History
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ padding: 2, my: 2 }}>
        <PageSubheader title="Hotel Bid Failure Log" />

        <Box mt={1}>
          <Button
            component={RouterLink}
            to={`/marketing/google-ads/hotel-bid-failure-log?customerId=${selectedCustomerId}`}
            variant="outlined"
            sx={{ mt: 2 }}
          >
            View Failure Log
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default HotelGroupView;
